'use client';

import React, { useEffect, useState } from 'react';
import { buildFacet, Facet, FacetState } from '@coveo/headless';
import { useCoveoEngine } from 'lib/utils/coveo/SearchContext/CoveoEngineContext';
import classNames from 'classnames';

type InitialsFacetProps = {
  field: string; // e.g., 'cwinitial'
  label?: string; // Label above the letters
};

const InitialsFacet = ({ field, label = 'First Letter' }: InitialsFacetProps) => {
  const engine = useCoveoEngine();
  const [facet, setFacet] = useState<Facet | null>(null);
  const [facetState, setFacetState] = useState<FacetState | null>(null);

  // Build facet on mount
  useEffect(() => {
    if (!engine) return;

    const newFacet = buildFacet(engine, {
      options: {
        field,
        facetId: field,
        numberOfValues: 26, // A-Z
        sortCriteria: 'alphanumeric',
      },
    });

    setFacet(newFacet);
    setFacetState(newFacet.state);

    const unsubscribe = newFacet.subscribe(() => {
      setFacetState({ ...newFacet.state });
    });

    return () => unsubscribe();
  }, [engine, field]);

  if (!facet || !facetState) return null;

  const onLetterClick = (letter: string) => {
    const match = facetState.values.find((v) => v.value === letter);
    if (match) {
      facet.toggleSelect(match);
    } else {
      // If not in list, apply a manual filter (optional)
    }
  };

  return (
    <div className="pb-[36px]">
      <div className="py-12 px-6 text-18 leading-[25px] font-semibold text-gray-900 font-nunitoSans ">
        {label}
      </div>
      <div className="px-6 lg:w-auto w-[288px]">
        <div className="grid lg:grid-cols-4 grid-cols-5 gap-4 ">
          {facetState.values.map((val) => (
            <button
              key={val.value}
              onClick={() => onLetterClick(val.value)}
              className={classNames(
                'rounded-[6px] border border-transparent bg-blue-50 h-[48px] w-[48px] text-center text-lg font-semibold',
                val.state === 'selected' ? 'bg-blue-600 text-white' : 'text-black hover:bg-blue-100'
              )}
            >
              {val.value}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default InitialsFacet;
